import moment from 'moment-timezone';
import { request, asyncListAll } from 'utilities/graph';
import {
  getElderDeliveryGroupByDeliveryGroupId,
  getElderServiceEventsByElderByType,
} from 'graphql/queries';
import {
  getElder,
} from './queries';
import { elderDeactivationReason } from '@silvergatedelivery/constants';
import { elderServiceEventConfig } from 'components/ElderServiceSchedule/helpers';
import { getRecurringOrderLogByRecurringOrderIdByDate, getOrder } from 'graphql/queries';
import { TIME_ZONE } from '@silvergatedelivery/constants';

const getDatesBetween = (startDate, endDate) => {
  const dates = [];
  const currentDate = moment(startDate);

  while (currentDate.isSameOrBefore(endDate, 'day')) {
    dates.push(currentDate.format('YYYY-MM-DD'));
    currentDate.add(1, 'day');
  }

  return dates;
};

const getElderServiceEvents = async (elderId, startOn, endOn) => {
  const start = moment(startOn).tz('Asia/Taipei').subtract(1, 'day').toISOString();
  const end = moment(endOn).tz('Asia/Taipei').add(1, 'day').toISOString();
  const [serviceStopEvents, leaveEvents, medicalEvents] = await Promise.all([
    asyncListAll(getElderServiceEventsByElderByType, {
      elderId,
      type: { eq: 'serviceStop' },
      filter: {
        status: { eq: '使用中' },
      },
    }),
    asyncListAll(getElderServiceEventsByElderByType, {
      elderId,
      type: { eq: 'leave' },
      filter: {
        and: [
          {
            status: { eq: '使用中' },
          },
          {
            startedAt: { lt: end },
          },
          {
            endedAt: { gt: start },
          },
        ],
      },
    }),
    asyncListAll(getElderServiceEventsByElderByType, {
      elderId,
      type: { eq: 'medical' },
      filter: {
        and: [
          {
            status: { eq: '使用中' },
          },
          {
            startedAt: { lt: end },
          },
          {
            endedAt: { gt: start },
          },
        ],
      },
    }),
  ]);
  return { serviceStopEvents, leaveEvents, medicalEvents };
};

export const processNewRecurringOrderSetting = async (data) => {
  const {
    startOn, endOn, elderId, restaurantId, deliveryStaffId, deliveryGroupId, mealItems, repeatOn, deliveryBy,
  } = data;
  const hour = parseInt(deliveryBy.split(':')[0]);
  data.mealSlot = hour <= 14 ? 'lunch' : 'dinner';

  // 建立30天訂單
  const today = moment().tz('Asia/Taipei').format('YYYY-MM-DD');
  const oneMonthAfter = moment().add(30, 'days').tz('Asia/Taipei').format('YYYY-MM-DD');
  const toCreateOrders = [];
  const toCancelOrdersByServiceEventInfo = [];
  const elders = [];
  if ((endOn && endOn < today) || data.status === '已停用') {
    return {
      toCreateOrders,
      elders,
      toCreateRecurringOrder: data,
    };
  }
  const start = startOn < today ? today : startOn;
  const end = (endOn && (endOn < oneMonthAfter)) ? endOn : oneMonthAfter;
  if (start <= end) {
    const datesBetween = getDatesBetween(start, end);
    if (elderId) {
      const { data: { getElder: data } } = await request(getElder, { id: elderId });
      if (data && data.status === '使用中') {
        const { serviceStopEvents, leaveEvents, medicalEvents } = await getElderServiceEvents(elderId, start, end);
        elders.push({
          ...data,
          serviceStopEvents,
          leaveEvents,
          medicalEvents,
        });
      }
    }
    if (deliveryGroupId) {
      const { data: { getElderDeliveryGroupByDeliveryGroupId: { items } } } =
        await request(getElderDeliveryGroupByDeliveryGroupId, { deliveryGroupId });
      if (items && items.length !== 0) {
        const getElderWithSortOrder = async (elderId, sortOrder) => {
          const { data: { getElder: data } } = await request(getElder, { id: elderId });
          if (data && data.status === '使用中') {
            const { serviceStopEvents, leaveEvents, medicalEvents } = await getElderServiceEvents(elderId, start, end);
            elders.push({
              ...data,
              deliveryGroupId,
              sortOrder,
              serviceStopEvents,
              leaveEvents,
              medicalEvents,
            });
          }
        };
        await Promise.all(items.map(({ elderId, sortOrder }) => {
          return getElderWithSortOrder(elderId, sortOrder);
        }));
      }
    }
    const hour = parseInt(deliveryBy.split(':')[0]);
    const mealSlot = hour <= 14 ? 'lunch' : 'dinner';
    const total = mealItems.reduce((accumulator, currentValue) => {
      return accumulator + currentValue.price * currentValue.quantity;
    }, 0);
    const totalCost = mealItems.reduce((accumulator, currentValue) => {
      return accumulator + currentValue.cost * currentValue.quantity;
    }, 0);
    elders.forEach((elder) => {
      const deliveryDatetimes = [];
      datesBetween.forEach((date) => {
        const week = moment(date).day();
        if (repeatOn.includes(week)) {
          if ((mealSlot === 'lunch' && elder.lunchRepeatOn && elder.lunchRepeatOn.includes(week)) ||
            (mealSlot === 'dinner' && elder.dinnerRepeatOn && elder.dinnerRepeatOn.includes(week))) {
            const deliveryDatetime = moment.tz(`${date} ${deliveryBy}`, 'Asia/Taipei').toISOString();
            let eventToCancelOrder = elder.serviceStopEvents.find((event) => {
              return moment(event.startedAt).tz('Asia/Taipei').isSameOrBefore(moment(deliveryDatetime));
            });
            if (!eventToCancelOrder) {
              eventToCancelOrder = elder.leaveEvents.find((event) => {
                return moment(event.startedAt).tz('Asia/Taipei').isSameOrBefore(moment(deliveryDatetime)) &&
                  moment(event.endedAt).tz('Asia/Taipei').isSameOrAfter(moment(deliveryDatetime)) &&
                  ((mealSlot === 'lunch' && event.lunchCancelled) || (mealSlot === 'dinner' && event.dinnerCancelled));
              });
              if (!eventToCancelOrder) {
                eventToCancelOrder = elder.medicalEvents.find((event) => {
                  return moment(event.startedAt).tz('Asia/Taipei').isSameOrBefore(moment(deliveryDatetime)) &&
                    moment(event.endedAt).tz('Asia/Taipei').isSameOrAfter(moment(deliveryDatetime)) &&
                    ((mealSlot === 'lunch' && event.lunchCancelled) || (mealSlot === 'dinner' && event.dinnerCancelled));
                });
              }
            }
            deliveryDatetimes.push(deliveryDatetime);
            if (eventToCancelOrder) {
              const eventType = eventToCancelOrder.type;
              const cancellationReason = elderServiceEventConfig[eventType].cancellationReason;
              let cancellationNote = eventToCancelOrder.description || elderServiceEventConfig[eventType].defaultNote;
              if (cancellationReason === 'serviceStop') {
                const deactivationReason = elderDeactivationReason.find(({ value }) => value === eventToCancelOrder.description);
                cancellationNote = deactivationReason?.label || elderServiceEventConfig[eventType].defaultNote;
              }
              toCancelOrdersByServiceEventInfo.push({
                elderId: elder.id,
                deliveryDatetime,
                restaurantId,
                cancellationReason,
                cancellationNote,
              });
            }
          }
        }
      });

      if (deliveryDatetimes.length !== 0) {
        toCreateOrders.push({
          elderId: elder.id,
          restaurantId,
          deliveryStaffId,
          mealItems,
          deliveryDatetimes,
          noteForDelivery: elder.noteForDelivery,
          noteForMeal: elder.noteForMeal,
          note: elder.note,
          deliveryGroupId: elder.deliveryGroupId,
          deliveryGroupSortOrder: elder.sortOrder,
          total,
          totalCost,
          deliveryStaffFee: 0,
          category: '送餐',
          paymentMethod: 'PAID',
          source: 'admin',
        });
      }
    });
  }
  return {
    toCreateOrders, // 所有原本週期性排單要新增的訂單
    elders,
    toCreateRecurringOrder: data,
    toCancelOrdersByServiceEventInfo, // 因為服務事件要取消的訂單
  };
};

export const processRecurringOrderReset = async (
  item,
) => {
  const { toCreateOrders, elders, toCreateRecurringOrder, toCancelOrdersByServiceEventInfo } = await processNewRecurringOrderSetting(item);
  const recurringOrderLogs = await asyncListAll(getRecurringOrderLogByRecurringOrderIdByDate,
    { recurringOrderId: toCreateRecurringOrder.id,
      date: { ge: moment().tz(TIME_ZONE).format('YYYY-MM-DD') } });
  const orderIds = recurringOrderLogs.map(({ orderIds }) => orderIds).flat();
  const orders = await Promise.all(orderIds.map(async (id) => {
    const { data: { getOrder: order } } = await request(getOrder, { id });
    return order;
  }));
  // 這五種狀態訂單不取消
  const ordersUncancelled = orders.filter((order) => !['completed', 'delivered', 'delivering', 'cancelled', 'readyForPickup'].includes(order.status));
  toCreateOrders.forEach((order) => {
    const newDeliveryDatetimes = [];
    const skippedDatetimes = [];
    order.deliveryDatetimes.forEach((datetime) => {
      const hour = moment(datetime).tz(TIME_ZONE).hour();
      const toCheckMealSlot = hour <= 14 ? 'lunch' : 'dinner';
      const toCheckDate = moment(datetime).tz(TIME_ZONE).format('YYYY-MM-DD');

      const ordersInSameTimeSlot = orders.filter(({ elderId, date, mealSlot }) =>
        elderId === order.elderId && date === toCheckDate && mealSlot === toCheckMealSlot,
      );
      const latestOrder = ordersInSameTimeSlot.reduce((latest, current) => {
        if (!latest) return current;
        return moment(current.updatedAt).isAfter(moment(latest.updatedAt)) ? current : latest;
      }, null);
      // 手動取消訂單後就不重新建立
      const orderCancelledManually = !!latestOrder && latestOrder.status === 'cancelled' && latestOrder.cancellationNote !== '重置週期性排單';
      // 進行中訂單不重新建立
      const orderInProgress = !!latestOrder && ['completed', 'delivered', 'delivering', 'readyForPickup'].includes(latestOrder.status);
      // 因為服務事件取消的訂單不重新建立
      const orderToCancelByServiceEvent =
        !!toCancelOrdersByServiceEventInfo.find((info) => info.elderId === order.elderId && info.deliveryDatetime === datetime);
      if (!orderCancelledManually && !orderInProgress && !orderToCancelByServiceEvent) {
        newDeliveryDatetimes.push(datetime);
      } else if (orderCancelledManually) {
        skippedDatetimes.push({
          datetime,
          reason: latestOrder.cancellationReason || '手動取消訂單',
          needToCreate: false,
        });
      }
    });
    Object.assign(order, {
      deliveryDatetimes: newDeliveryDatetimes,
      skippedDatetimes: skippedDatetimes,
    });
  });

  return {
    toCreateOrders,
    ordersUncancelled,
    elders,
    toCreateRecurringOrder,
  };
};
